import { useState } from 'react';
import { CUSTOMER_API_URL } from '../../../../config';

/**
 * Partner Key Validation Form
 *
 * This is the first page of the dashboard where users enter their Refersion affiliate ID.
 * The ID is validated against the Refersion API and checked for approved offer eligibility
 * before allowing access to the dashboard.
 */

interface PartnerKeyFormProps {
  onValidated: (affiliateId: string) => void;
}

export default function PartnerKeyForm({ onValidated }: PartnerKeyFormProps) {
  const [partnerKey, setPartnerKey] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!partnerKey.trim()) {
      setError('Please enter your affiliate ID');
      return;
    }

    setIsValidating(true);
    setError(null);

    try {
      // Call Refersion validation endpoint
      const response = await fetch(
        `${CUSTOMER_API_URL}/refersion/validate-affiliate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ affiliateId: partnerKey.trim() })
        }
      );

      const data = await response.json();

      if (data.valid) {
        // Validation successful - affiliate has approved offer
        onValidated(partnerKey);
      } else {
        // Validation failed - show specific error message
        setError(
          data.error || 'Invalid affiliate ID. Please check and try again.'
        );
      }
    } catch (err) {
      console.error('Error validating affiliate:', err);
      setError('Failed to validate affiliate ID. Please try again.');
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#DFEDED] px-4">
      <div className="w-full max-w-md rounded-lg bg-white p-8 shadow-lg">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="mb-2 text-3xl font-semibold text-[#014876]">
            NameBubbles Partner Dashboard
          </h1>
          <p
            className="text-gray-600"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            Enter your Refersion affiliate ID to continue
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label
              htmlFor="partnerKey"
              className="mb-2 block text-sm font-medium text-gray-700"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Affiliate ID
            </label>
            <input
              type="text"
              id="partnerKey"
              value={partnerKey}
              onChange={e => setPartnerKey(e.target.value)}
              placeholder="Enter your Refersion affiliate ID"
              className="box-border w-full rounded-lg border border-gray-300 px-1 py-1 outline-none transition focus:border-transparent focus:ring-2 focus:ring-[#014876]"
              style={{ fontFamily: 'Poppins, sans-serif' }}
              disabled={isValidating}
            />
            {error && (
              <p
                className="mt-2 text-sm text-red-600"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                {error}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={isValidating}
            className="w-full rounded-lg bg-[#014876] px-4 py-3 font-semibold text-white transition hover:bg-[#013456] disabled:cursor-not-allowed disabled:opacity-50"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            {isValidating ? 'Validating...' : 'Continue'}
          </button>
        </form>

        {/* Help Text */}
        <div className="mt-6 text-center">
          <p
            className="text-sm text-gray-500"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            Don't have an affiliate ID?{' '}
            <a href="#" className="text-[#014876] hover:underline">
              Contact us
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
